import { useEffect, useMemo, useState } from "react";
import {
  Power, Save, RefreshCw, Calendar, Calculator, Info, Users, Building2,
  HeartHandshake, AlertTriangle, CheckCircle2, History, ChevronRight, Plus, Trash2
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription
} from "@/components/ui/dialog";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from "recharts";
import { toast } from "sonner";
import { Label } from "@/components/ui/label";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useSHUConfig, metaFor } from "@/lib/shuConfig";
import { formatRupiah } from "@/lib/dummyData";
import { ShuAPI } from "@/lib/api";

const SHU_POOL = 1240000000; // Rp 1.24 Miliar — contoh keuntungan organisasi Gerai yang akan dibagi

const categoryColor = {
  "Internal": "bg-forest/10 text-forest border-forest/25",
  "Anggota": "bg-amber-warm/15 text-amber-700 border-amber-warm/30",
  "Sosial": "bg-terracotta/10 text-terracotta border-terracotta/25",
};

const pieColors = ["#2D5A27", "#D48C45", "#85A37E", "#A94C38", "#4A7A8C", "#BF7C3A", "#3A7233", "#8E3E2D", "#A8C0A2"];

export default function AdminSHU() {
  const { enabled, setEnabled, config, updateRow, setAll, resetDefault, year, setYear, totalPct, memberPct } = useSHUConfig();
  const [confirmReset, setConfirmReset] = useState(false);
  // CRUD kategori (tambah/hapus) — Super Admin.
  const [addOpen, setAddOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newPct, setNewPct] = useState(0);
  const [newMemberShare, setNewMemberShare] = useState(false);
  const [delRow, setDelRow] = useState(null);
  const [busy, setBusy] = useState(false);

  // Muat kategori pembagian keuntungan organisasi dari backend saat halaman dibuka.
  const reload = () => ShuAPI.config()
    .then((d) => setAll(d.config || []))
    .catch(() => { /* biarkan default lokal */ });
  useEffect(() => { reload(); }, [setAll]); // eslint-disable-line react-hooks/exhaustive-deps

  const addCategory = async () => {
    if (!newName.trim()) return toast.error("Nama kategori wajib diisi");
    setBusy(true);
    try {
      await ShuAPI.addCategory({
        display_name: newName.trim(),
        percentage_allocation: Number(newPct) || 0,
        is_member_share: newMemberShare,
      });
      toast.success(`Kategori "${newName.trim()}" ditambahkan — atur persentase hingga total 100% lalu Terapkan`);
      setAddOpen(false); setNewName(""); setNewPct(0); setNewMemberShare(false);
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Gagal menambah kategori");
    } finally { setBusy(false); }
  };

  const confirmDelete = async () => {
    setBusy(true);
    try {
      await ShuAPI.deleteCategory(delRow.feature_key);
      toast.success(`Kategori "${delRow.display_name}" dihapus — tata ulang persentase hingga 100% lalu Terapkan`);
      setDelRow(null);
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Gagal menghapus kategori");
    } finally { setBusy(false); }
  };

  const piData = useMemo(
    () => config.filter((c) => c.is_active && c.percentage_allocation > 0).map((c) => ({
      name: c.display_name,
      value: c.percentage_allocation,
    })),
    [config]
  );

  const handleSave = async () => {
    if (Math.abs(totalPct - 100) > 0.01) {
      toast.error(`Total alokasi harus 100% — sekarang ${totalPct.toFixed(1)}%`);
      return;
    }
    try {
      // Simpan ke backend (tabel shu_allocation_settings) — langsung berlaku ke seluruh anggota.
      await ShuAPI.updateConfig(config.map((c) => ({
        feature_key: c.feature_key,
        is_active: c.is_active,
        percentage_allocation: c.percentage_allocation,
        is_member_share: c.is_member_share,
      })));
      toast.success(`Pembagian keuntungan organisasi ${year} disimpan & diberlakukan`);
    } catch (err) {
      toast.error(err?.response?.data?.error || "Gagal menyimpan konfigurasi");
    }
  };

  const handlePctChange = (key, val) => updateRow(key, { percentage_allocation: Math.max(0, Math.min(100, Number(val))) });

  const isValid = Math.abs(totalPct - 100) < 0.01;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="font-display text-3xl lg:text-4xl font-bold text-ink tracking-tight">Pembagian Keuntungan Organisasi</h1>
            <Badge className="bg-amber-warm text-ink hover:bg-amber-warm rounded-full text-[10px] uppercase tracking-wider">Model Bisnis 5.2</Badge>
          </div>
          <p className="text-ink/65 mt-2 text-sm max-w-2xl">
            Atur pembagian keuntungan organisasi di level Gerai: Pengembangan Usaha, Dana Sosial, Bonus
            Karyawan, Bonus Pimpinan Gerai, dan SHU Anggota. Kategori dapat ditambah/dihapus; total kategori
            aktif wajib 100%.
          </p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <div className="flex items-center gap-2 bg-white border border-stone-soft rounded-full px-4 py-2">
            <Power size={15} className={enabled ? "text-forest" : "text-terracotta"} />
            <span className="text-xs font-semibold uppercase tracking-wider">Sistem SHU</span>
            <Switch checked={enabled} onCheckedChange={(v) => {
              setEnabled(v);
              toast[v ? "success" : "error"](v ? "SHU AKTIF — Anggota melihat estimasi SHU" : "SHU NONAKTIF — Anggota melihat pengumuman");
            }} data-testid="shu-master-toggle" />
          </div>
          <div className="text-[11px] text-ink/55">
            {enabled ? "Status: berjalan normal" : "Status: dihentikan sementara"}
          </div>
        </div>
      </div>

      {/* Period selector + KPI */}
      <div className="grid lg:grid-cols-4 gap-4">
        <Card className="rounded-2xl border-stone-soft p-5">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-ink/55 font-semibold">
            <Calendar size={13} /> Tahun Buku
          </div>
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger className="mt-2 rounded-lg border-stone-soft bg-white font-display font-bold text-2xl text-ink h-12" data-testid="shu-year-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[2023, 2024, 2025, 2026].map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
        </Card>
        <Card className="rounded-2xl border-stone-soft p-5">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-ink/55 font-semibold">
            <Calculator size={13} /> Keuntungan Dibagi (Contoh)
          </div>
          <div className="font-display font-bold text-2xl text-ink mt-3">{formatRupiah(SHU_POOL)}</div>
          <div className="text-[11px] text-ink/55 mt-1">Surplus tahun {year}</div>
        </Card>
        <Card className="rounded-2xl border-stone-soft p-5">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-ink/55 font-semibold">
            <Users size={13} /> Bagian Anggota
          </div>
          <div className="font-display font-bold text-2xl mt-3" style={{ color: "#A86A2E" }}>{memberPct.toFixed(1)}%</div>
          <div className="text-[11px] text-ink/55 mt-1">≈ {formatRupiah(SHU_POOL * memberPct / 100)}</div>
        </Card>
        <Card className={`rounded-2xl border-2 p-5 ${isValid ? "border-forest bg-forest/5" : "border-terracotta bg-terracotta/5"}`} data-testid="shu-total-validation">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider font-semibold" style={{ color: isValid ? "#2D5A27" : "#A94C38" }}>
            {isValid ? <CheckCircle2 size={13} /> : <AlertTriangle size={13} />} Total Alokasi
          </div>
          <div className="font-display font-bold text-2xl mt-3" style={{ color: isValid ? "#2D5A27" : "#A94C38" }}>{totalPct.toFixed(1)}%</div>
          <Progress value={Math.min(100, totalPct)} className="h-2 mt-2" />
          <div className="text-[11px] mt-1" style={{ color: isValid ? "#2D5A27" : "#A94C38" }}>
            {isValid ? "Valid — siap diberlakukan" : `Harus 100% (selisih ${(100 - totalPct).toFixed(1)}%)`}
          </div>
        </Card>
      </div>

      {/* Disabled overlay info */}
      {!enabled && (
        <Card className="rounded-2xl border-2 border-terracotta/30 bg-terracotta/5 p-5 flex gap-4 items-start animate-fade-up">
          <AlertTriangle className="text-terracotta shrink-0 mt-0.5" size={20} />
          <div>
            <div className="font-display font-semibold text-terracotta">Sistem SHU dimatikan sementara</div>
            <p className="text-sm text-ink/75 mt-1">
              Konfigurasi tetap bisa diedit, namun perhitungan dan pembagian SHU dijeda. Halaman SHU Tracker
              di akun anggota akan menampilkan pengumuman bahwa SHU sedang dalam evaluasi.
            </p>
          </div>
        </Card>
      )}

      {/* Main config table */}
      <div className="grid lg:grid-cols-3 gap-5">
        <Card className="lg:col-span-2 rounded-2xl border-stone-soft p-6">
          <div className="flex items-center justify-between mb-5 gap-2 flex-wrap">
            <div>
              <h3 className="font-display font-semibold text-ink">Kategori Pembagian Keuntungan</h3>
              <p className="text-xs text-ink/55 mt-1">Tambah/hapus kategori, toggle aktif, atur persentase, dan tandai kategori bagian anggota</p>
            </div>
            <div className="flex gap-2">
            <Button size="sm" className="rounded-full bg-forest hover:bg-forest-dark text-white" onClick={() => setAddOpen(true)} data-testid="shu-add-category">
              <Plus size={13} /> Tambah Kategori
            </Button>
            <Dialog open={confirmReset} onOpenChange={setConfirmReset}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm" className="rounded-full border-stone-soft" data-testid="shu-reset"><RefreshCw size={13} /> Reset Default</Button>
              </DialogTrigger>
              <DialogContent className="rounded-2xl max-w-md">
                <DialogHeader>
                  <DialogTitle>Reset ke konfigurasi default?</DialogTitle>
                  <DialogDescription>Semua perubahan persentase & toggle akan dikembalikan ke nilai default tahun {year}.</DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <Button variant="outline" className="rounded-full" onClick={() => setConfirmReset(false)}>Batal</Button>
                  <Button className="rounded-full bg-forest hover:bg-forest-dark text-white" onClick={() => { resetDefault(); setConfirmReset(false); toast.success("Konfigurasi direset"); }} data-testid="shu-reset-confirm">Ya, Reset</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            </div>
          </div>

          <div className="space-y-3">
            {config.map((row) => {
              const meta = metaFor(row.feature_key);
              return (
                <div
                  key={row.feature_key}
                  className={`rounded-xl border-2 p-4 transition-all ${row.is_active ? "border-stone-soft bg-white" : "border-stone-soft/60 bg-cream/50 opacity-60"}`}
                  data-testid={`shu-row-${row.feature_key}`}
                >
                  <div className="flex items-start gap-4">
                    <div className="text-2xl shrink-0 leading-none mt-1">{meta.icon}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="font-display font-semibold text-ink">{row.display_name}</div>
                        <Badge className={`${categoryColor[meta.category]} hover:${categoryColor[meta.category]} rounded-full text-[10px] uppercase tracking-wider border`}>{meta.category}</Badge>
                        {row.is_member_share && row.is_active && (
                          <Badge className="bg-amber-warm/20 text-amber-700 hover:bg-amber-warm/20 rounded-full text-[10px] uppercase tracking-wider">
                            <Users size={9} /> Bagian Anggota
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-ink/60 mt-1 leading-relaxed">{meta.desc}</p>
                      <code className="text-[10px] text-ink/40 font-mono mt-1 inline-block">{row.feature_key}</code>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <Switch
                        checked={row.is_active}
                        onCheckedChange={(v) => { updateRow(row.feature_key, { is_active: v }); toast.success(`${row.display_name} ${v ? "diaktifkan" : "dinonaktifkan"}`); }}
                        data-testid={`shu-active-${row.feature_key}`}
                      />
                      <Button
                        variant="ghost" size="icon"
                        className="rounded-lg hover:bg-terracotta/10 text-ink/50 hover:text-terracotta h-8 w-8"
                        onClick={() => setDelRow(row)}
                        title="Hapus kategori"
                        data-testid={`shu-delete-${row.feature_key}`}
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </div>

                  {row.is_active && (
                    <div className="mt-4 grid sm:grid-cols-[1fr_auto] gap-3 items-center pl-12">
                      <div>
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-[11px] uppercase tracking-wider text-ink/55 font-semibold">Persentase Alokasi</span>
                          <span className="text-xs text-ink/55">≈ {formatRupiah(SHU_POOL * row.percentage_allocation / 100)}</span>
                        </div>
                        <Slider
                          value={[Number(row.percentage_allocation)]}
                          onValueChange={([v]) => handlePctChange(row.feature_key, v)}
                          min={0} max={50} step={0.5}
                          className=""
                          data-testid={`shu-pct-slider-${row.feature_key}`}
                        />
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="relative">
                          <Input
                            type="number"
                            value={row.percentage_allocation}
                            onChange={(e) => handlePctChange(row.feature_key, e.target.value)}
                            className="w-24 h-9 pr-7 text-right font-display font-bold text-ink rounded-lg"
                            min={0} max={100} step={0.5}
                            data-testid={`shu-pct-input-${row.feature_key}`}
                          />
                          <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-ink/55 text-sm">%</span>
                        </div>
                        <label className="flex items-center gap-1.5 text-[11px] text-ink/65 cursor-pointer whitespace-nowrap">
                          <Switch
                            checked={row.is_member_share}
                            onCheckedChange={(v) => updateRow(row.feature_key, { is_member_share: v })}
                            className="scale-75"
                            data-testid={`shu-membershare-${row.feature_key}`}
                          />
                          Bagi anggota
                        </label>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Card>

        {/* Right: pie + actions */}
        <div className="space-y-5">
          <Card className="rounded-2xl border-stone-soft p-6">
            <h3 className="font-display font-semibold text-ink">Visualisasi Alokasi</h3>
            <p className="text-xs text-ink/55 mt-1">Total {totalPct.toFixed(1)}% dari keuntungan organisasi Gerai</p>
            <div className="h-56 mt-2">
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={piData} dataKey="value" nameKey="name" outerRadius={75} innerRadius={45} paddingAngle={2}>
                    {piData.map((_, i) => <Cell key={i} fill={pieColors[i % pieColors.length]} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="space-y-1.5">
              {piData.map((p, i) => (
                <div key={p.name} className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: pieColors[i % pieColors.length] }}></span>
                    <span className="text-ink/75 truncate">{p.name}</span>
                  </div>
                  <span className="font-semibold text-ink shrink-0 ml-2">{p.value.toFixed(1)}%</span>
                </div>
              ))}
            </div>
          </Card>

          <Card className="rounded-2xl border-stone-soft p-6 bg-forest text-cream relative overflow-hidden blob-bg">
            <div className="relative z-10">
              <div className="text-xs uppercase tracking-[0.22em] text-amber-warm font-semibold flex items-center gap-1">
                <Info size={13} /> Simulasi Anggota
              </div>
              <h4 className="font-display font-semibold mt-2">Estimasi untuk Annisa</h4>
              <p className="text-cream/85 text-xs mt-1">Simpanan Rp 16.5jt · belanja 12 transaksi tahun ini</p>
              <div className="font-display font-bold text-3xl mt-3 text-amber-warm">
                {enabled ? formatRupiah(Math.round((SHU_POOL * memberPct / 100) / 4827)) : "Rp 0"}
              </div>
              <div className="text-xs text-cream/75 mt-1">
                {enabled ? `${memberPct.toFixed(1)}% (SHU Anggota) × keuntungan ÷ ${4827} anggota (rata-rata)` : "SHU sedang dinonaktifkan"}
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Sticky save bar */}
      <div className="sticky bottom-4 z-20">
        <Card className="rounded-2xl border-stone-soft p-4 bg-white shadow-2xl flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-3 text-sm">
            <History size={16} className="text-ink/55" />
            <span className="text-ink/65">Terakhir diperbarui · 2 jam lalu oleh <span className="font-semibold text-ink">Rahmat Hidayat</span></span>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" className="rounded-full border-stone-soft" onClick={() => toast.info("Riwayat audit dibuka")} data-testid="shu-audit">Audit Trail</Button>
            <Button
              onClick={handleSave}
              disabled={!isValid}
              className="rounded-full bg-forest hover:bg-forest-dark text-white disabled:bg-stone-soft disabled:text-ink/40"
              data-testid="shu-save"
            >
              <Save size={15} /> Terapkan Konfigurasi
            </Button>
          </div>
        </Card>
      </div>

      {/* Dialog Tambah Kategori */}
      <Dialog open={addOpen} onOpenChange={(v) => { setAddOpen(v); if (!v) { setNewName(""); setNewPct(0); setNewMemberShare(false); } }}>
        <DialogContent className="rounded-2xl max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">Tambah Kategori Pembagian</DialogTitle>
            <DialogDescription>
              Kategori baru aktif dengan persentase awal yang Anda isi. Setelah ditambah, tata ulang persentase
              seluruh kategori hingga total 100%, lalu klik "Terapkan Konfigurasi".
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-ink/55">Nama Kategori</Label>
              <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="cth. Dana Pendidikan" className="rounded-lg" data-testid="shu-new-name" />
            </div>
            <div className="grid grid-cols-2 gap-3 items-end">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-ink/55">Persentase Awal</Label>
                <div className="relative">
                  <Input
                    type="number" min={0} max={100} step={0.5} value={newPct}
                    onChange={(e) => setNewPct(e.target.value)}
                    className="rounded-lg pr-7 text-right" data-testid="shu-new-pct"
                  />
                  <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-ink/55 text-sm">%</span>
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm text-ink/70 cursor-pointer pb-2">
                <Switch checked={newMemberShare} onCheckedChange={setNewMemberShare} data-testid="shu-new-membershare" />
                Bagian anggota
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setAddOpen(false)}>Batal</Button>
            <Button className="rounded-full bg-forest hover:bg-forest-dark text-white" onClick={addCategory} disabled={busy} data-testid="shu-new-submit">
              {busy ? "Menambahkan…" : "Tambah Kategori"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Konfirmasi Hapus Kategori */}
      <AlertDialog open={!!delRow} onOpenChange={(v) => !v && setDelRow(null)}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">Hapus kategori "{delRow?.display_name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              Kategori akan dihapus permanen dari pembagian keuntungan organisasi. Setelah dihapus, tata ulang
              persentase kategori lain hingga total kembali 100%, lalu Terapkan Konfigurasi.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Batal</AlertDialogCancel>
            <AlertDialogAction className="rounded-full bg-terracotta hover:bg-terracotta/90" onClick={confirmDelete} disabled={busy} data-testid="shu-delete-confirm">
              {busy ? "Menghapus…" : "Hapus"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
